import React from 'react';
import { ArrowRight, Radar, Stethoscope, CheckCircle2, Wrench, Activity, ListChecks } from 'lucide-react';

const HIT_COMPONENTS = [
  { label: 'Songwriting & Hook', score: 74 },
  { label: 'Emotional Impact', score: 68 },
  { label: 'Production & Sonics', score: 72 },
  { label: 'Commercial Fit', score: 66 },
];

const PreviewShell = ({ title, badge, testId, children }) => (
  <div
    className="relative border border-avenue-white/10 bg-avenue-carbon overflow-hidden"
    data-testid={testId}
  >
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-avenue-white/10">
      <span className="text-[11px] uppercase tracking-[0.2em] text-avenue-white/50 truncate">{title}</span>
      <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] font-medium px-2 py-0.5 border border-avenue-red/50 text-avenue-red">
        {badge}
      </span>
    </div>
    <div className="px-5 pt-5 pb-10 space-y-5">{children}</div>
    {/* Truncation fade — the rest of the report lives in the artist dashboard */}
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-avenue-carbon via-avenue-carbon/90 to-transparent"
      aria-hidden="true"
    />
    <div className="absolute inset-x-0 bottom-0 px-5 py-3 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-avenue-white/40">
      <span>Sample excerpt</span>
      <span className="text-avenue-red">Full report in your dashboard</span>
    </div>
  </div>
);

const HitAnalystPreview = () => (
  <PreviewShell title="Hit Radar · “Here Comes The Sun”" badge="Report Released" testId="hit-analyst-preview">
    <div className="flex items-end gap-5">
      <div className="flex items-baseline gap-1">
        <span className="font-display text-6xl leading-none text-avenue-red">70</span>
        <span className="text-sm text-avenue-white/50">/100</span>
      </div>
      <div className="pb-1">
        <p className="text-[11px] uppercase tracking-[0.2em] text-avenue-white/50">Hit Potential</p>
        <p className="font-editorial text-xl text-avenue-white leading-tight">Promising — Shape the Demo</p>
      </div>
    </div>
    <ul className="space-y-2.5">
      {HIT_COMPONENTS.map((c) => (
        <li key={c.label}>
          <div className="flex items-center justify-between text-xs text-avenue-white/70 mb-1">
            <span>{c.label}</span>
            <span className="font-display text-avenue-white">{c.score}</span>
          </div>
          <div className="h-px bg-avenue-white/10">
            <div className="h-px bg-avenue-red" style={{ width: `${c.score}%` }} />
          </div>
        </li>
      ))}
    </ul>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      <div>
        <h4 className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-avenue-white/50 mb-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-avenue-red" /> Strengths
        </h4>
        <p className="text-avenue-white/70 leading-relaxed">
          The chorus lands on the first listen — a clear, singable hook with a lyric executives can repeat back.
        </p>
      </div>
      <div>
        <h4 className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-avenue-white/50 mb-2">
          <Wrench className="w-3.5 h-3.5 text-avenue-red" /> Revisions
        </h4>
        <p className="text-avenue-white/70 leading-relaxed">
          Verse two drags before the lift. Tighten eight bars and bring the vocal forward in the mix so the…
        </p>
      </div>
    </div>
  </PreviewShell>
);

const DemoDoctorPreview = () => (
  <PreviewShell title="Demo Audit · “Here Comes The Sun”" badge="Audit Released" testId="demo-doctor-preview">
    <div className="flex items-end gap-5">
      <span className="font-display text-6xl leading-none text-avenue-red">B-</span>
      <div className="pb-1">
        <p className="text-[11px] uppercase tracking-[0.2em] text-avenue-white/50">Pitch-Readiness</p>
        <p className="font-editorial text-xl text-avenue-white leading-tight">
          77<span className="text-avenue-white/40 text-base">/100</span> — Nearly Desk-Ready
        </p>
      </div>
    </div>
    <p className="text-sm text-avenue-white/70 leading-relaxed">
      <span className="text-avenue-white">First impression:</span> Warm, confident vocal and a hook that
      arrives on time. The low end is crowding the vocal in the chorus and the demo fades out instead of ending.
    </p>
    <div>
      <h4 className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-avenue-white/50 mb-2">
        <Activity className="w-3.5 h-3.5 text-avenue-red" /> Diagnosis
      </h4>
      <ul className="space-y-2 text-sm">
        <li className="flex gap-3">
          <span className="mt-2.5 h-px w-4 bg-avenue-red shrink-0" aria-hidden="true" />
          <span className="text-avenue-white/70">
            <span className="text-avenue-white">Mix:</span> kick and bass mask the lead vocal from 0:58 — carve 2–3 dB around 200 Hz.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="mt-2.5 h-px w-4 bg-avenue-red shrink-0" aria-hidden="true" />
          <span className="text-avenue-white/70">
            <span className="text-avenue-white">Arrangement:</span> the bridge repeats the pre-chorus idea — give an executive one new moment.
          </span>
        </li>
      </ul>
    </div>
    <div>
      <h4 className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-avenue-white/50 mb-2">
        <ListChecks className="w-3.5 h-3.5 text-avenue-red" /> Treatment Plan
      </h4>
      <p className="text-sm text-avenue-white/70 leading-relaxed">
        1. Re-balance the chorus so the vocal sits on top. 2. Write a hard ending — never fade a pitch. 3. Trim the intro to…
      </p>
    </div>
  </PreviewShell>
);

const SERVICES = [
  {
    key: 'hit-analyst',
    num: '01',
    icon: Radar,
    title: 'Avenue AR Hit Radar',
    tagline: 'Know your score before they do.',
    pitch:
      'Before your song ever reaches a major A&R executive, the Avenue AR team scores it the way a label would — hook, emotional impact, production, commercial fit and release readiness — in one detailed commercial report.',
    bullets: [
      'Scored against real A&R criteria and genre benchmarks.',
      'Clear strengths, risks and revisions you can act on today.',
      'Score 70% or higher and your song enters the Hit List — everyone on the platform will know your record has hit potential.',
    ],
    ctaText: 'Analyze My Song',
    ctaTestId: 'hit-analyst-cta',
    Preview: HitAnalystPreview,
  },
  {
    key: 'demo-doctor',
    num: '02',
    icon: Stethoscope,
    title: 'Demo Doctor',
    tagline: 'Polish the demo. Get the call.',
    pitch:
      'Executives decide in the first thirty seconds. Our team examines your recording, mix, arrangement and presentation against what today’s decision-makers expect to hear — then hands you a treatment plan to fix it before you pitch.',
    bullets: [
      'A letter grade and pitch-readiness score for your demo.',
      'A diagnosis of what is holding the record back — and how to fix it.',
      'Fix the issues before an executive hears them, not after.',
    ],
    ctaText: 'Run Demo Doctor',
    ctaTestId: 'demo-doctor-cta',
    Preview: DemoDoctorPreview,
  },
];

const ServicePanel = ({ service, onCta }) => {
  const { num, icon: Icon, title, tagline, pitch, bullets, ctaText, ctaTestId, Preview, key: slug } = service;
  return (
    <div className="bg-avenue-black p-6 md:p-10 flex flex-col gap-8" data-testid={`service-panel-${slug}`}>
      <div>
        <div className="flex items-center gap-3">
          <span className="font-display text-[clamp(2.5rem,5vw,3.5rem)] leading-none text-avenue-red">{num}</span>
          <span className="w-9 h-9 border border-avenue-white/15 flex items-center justify-center text-avenue-red">
            <Icon className="w-4 h-4" />
          </span>
        </div>
        <h3 className="mt-5 font-editorial text-3xl md:text-4xl text-avenue-white tracking-[-0.02em]">{title}</h3>
        <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-avenue-white/50">{tagline}</p>
        <p className="mt-5 text-base text-avenue-white/70 leading-relaxed">{pitch}</p>
        <ul className="mt-5">
          {bullets.map((b, i) => (
            <li key={i} className="border-t border-avenue-white/10 py-3 flex items-start gap-4">
              <span className="mt-2.5 h-px w-5 bg-avenue-red shrink-0" aria-hidden="true" />
              <span className="text-sm md:text-[15px] text-avenue-white/70 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <Preview />

      <div className="mt-auto pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          onClick={onCta}
          className="group inline-flex items-center justify-center gap-2 font-medium uppercase tracking-wider bg-avenue-red text-avenue-white px-6 py-3 text-sm hover:bg-avenue-electric-red transition-colors duration-300"
          data-testid={ctaTestId}
        >
          <span>{ctaText}</span>
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <span className="text-[11px] uppercase tracking-[0.2em] text-avenue-white/40">Optional — submit directly any time</span>
      </div>
    </div>
  );
};

// Split-screen pitch for the two pre-submission services (Hit Analyst left,
// Demo Doctor right). Sits above the Discover rows on the landing page.
export default function ServiceSplit({ onCta }) {
  return (
    <div className="px-6 md:px-10" data-testid="services-split-section">
      <div className="mb-8 md:mb-10 max-w-2xl">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-avenue-red block">Before the Executive Presses Play</span>
        <span aria-hidden="true" className="block h-px bg-avenue-red mt-4 w-12" />
        <h3 className="mt-6 font-editorial text-2xl md:text-3xl text-avenue-white tracking-[-0.02em]">
          Our team evaluates your music <span className="text-avenue-red">before</span> it reaches major A&amp;R executives.
        </h3>
        <p className="mt-4 text-base text-avenue-white/70 leading-relaxed">
          Two optional services built for artists who want to pitch with confidence. Get the honest read, make the
          fixes, then submit knowing your record is ready for the room.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-avenue-white/10 border border-avenue-white/10">
        {SERVICES.map((s) => (
          <ServicePanel key={s.key} service={s} onCta={onCta} />
        ))}
      </div>
    </div>
  );
}
